<script setup lang="ts">
import Swiper from 'swiper'
import { Thumbs } from 'swiper/modules'
import 'swiper/css'
import {
  BODY_TYPE_TABS,
  CATALOG_SEARCH_TABS,
  findBodyTypeTab,
  type BodyTypeTabTitle,
  type CatalogSearchTabTitle,
} from '@/constant/brandCarParams'
import type { BodyTypesResponse } from '#shared/interfaces/top/bodyTypes'
import { useCatalogSearchStore } from '@/stores/catalogSearch'
import { buildAsyncDataKey } from '@/utils/asyncDataKey'

const showBrandsFlg = ref(false)
const pagePosition = useState('page_position', () => 0)
const catalogSearchStore = useCatalogSearchStore()

// タブ名をキーにして保持する。インデックスで対応付けると、取得に失敗したタブの分だけ
// 以降のタブがずれて別タブの内容を表示してしまうため。
// $fetch を useAsyncData のハンドラ内にまとめることで、並列取得を維持したまま
// payload 転送を効かせ、ハイドレーション後の再取得を防ぐ
const { data: bodyTypesByTab } = await useAsyncData(
  buildAsyncDataKey(
    'catalogs-body-types',
    'tabs',
    BODY_TYPE_TABS.map((tab) => tab.title).join(',')
  ),
  async () => {
    const results = await Promise.all(
      BODY_TYPE_TABS.map((tab) =>
        $fetch<BodyTypesResponse>('/api/catalogs/bodyTypes', {
          cache: 'no-cache',
          // ボディタイプタブは全件が対象のため use_type を持たない
          query: tab.useType ? { use_type: tab.useType } : {},
        })
      )
    )
    const contents: Partial<Record<BodyTypeTabTitle, BodyTypesResponse>> = {}
    BODY_TYPE_TABS.forEach((tab, index) => {
      const result = results[index]
      if (result) {
        contents[tab.title] = result
      }
    })
    return contents
  }
)
const contentsByTabTitle = computed<
  Partial<Record<BodyTypeTabTitle, BodyTypesResponse>>
>(() => bodyTypesByTab.value ?? {})

/**
 * 表示中のボディタイプ系タブ。メーカータブ表示中は undefined。
 * 表示中タブの情報源をストアの selectedTabTitle だけに寄せ、スライド位置から
 * 別途保持しないことで、両者が食い違う余地をなくしている。
 */
const activeBodyTypeTab = computed(() =>
  findBodyTypeTab(catalogSearchStore.selectedTabTitle)
)

/** タブ名に対応するスライド位置(CATALOG_SEARCH_TABS のインデックス) */
const toSlidePosition = (tabTitle: CatalogSearchTabTitle): number =>
  CATALOG_SEARCH_TABS.findIndex((tab) => tab.title === tabTitle)

// Swiper インスタンスは unmount 時に破棄する。破棄しないと slideChange ハンドラが
// ストアへの参照を保持したまま残り、リークしたインスタンス経由で表示中タブが
// 書き換わる余地が残るため
let tabSwiper: Swiper | undefined
let contentsSwiper: Swiper | undefined
// nextTick のコールバックが unmount 後に走ると、生成したインスタンスを
// onUnmounted では破棄できなくなるため、マウント状態を見て生成自体を打ち切る
let isMounted = false

const initSwiper = (initialSlide: number) => {
  nextTick(() => {
    if (!isMounted) {
      return
    }
    tabSwiper = new Swiper('.swiper-brand-car', {
      slidesPerView: 'auto',
      slidesOffsetBefore: 10,
      breakpoints: {
        320: {
          spaceBetween: 19,
          slidesOffsetAfter: 10,
        },
        360: {
          spaceBetween: 28,
          slidesOffsetAfter: 10,
        },
        375: {
          spaceBetween: 31,
        },
        390: {
          spaceBetween: 35,
        },
        414: {
          spaceBetween: 40,
        },
        428: {
          spaceBetween: 45,
        },
        430: {
          spaceBetween: 45,
        },
      },
    })

    contentsSwiper = new Swiper('.swiper-contents', {
      autoHeight: true,
      modules: [Thumbs],
      slidesPerView: 1,
      speed: 500,
      spaceBetween: 10,
      initialSlide,
      thumbs: {
        swiper: tabSwiper,
      },
      on: {
        slideChange: (swiper) => {
          const tab = CATALOG_SEARCH_TABS[swiper.activeIndex]
          if (tab) {
            catalogSearchStore.setSelectedTabTitle(tab.title)
          }
        },
      },
    })
  })
}

onUnmounted(() => {
  isMounted = false
  // contentsSwiper は thumbs で tabSwiper を参照しているため先に破棄する
  contentsSwiper?.destroy()
  contentsSwiper = undefined
  tabSwiper?.destroy()
  tabSwiper = undefined
})

onMounted(() => {
  isMounted = true
  pagePosition.value = window.history.state.position
  showBrandsFlg.value = true
  // ストアのタブ名は必ず CATALOG_SEARCH_TABS のいずれかなので、位置は必ず見つかる
  initSwiper(toSlidePosition(catalogSearchStore.selectedTabTitle))
})

const changeDisplayIndex = (index: number) => {
  if (Number(index) >= 0) {
    catalogSearchStore.setDisplayIndex(index)
  }
}

const nuxtApp = useNuxtApp()
const sendEventGaTag = () => {
  nuxtApp.$sendEventGaTag('CatalogCatalogSearchTap')
}
</script>

<template>
  <div class="container">
    <div class="fw-bold">カタログ検索</div>
  </div>
  <div class="swiper-box border-top border-bottom">
    <div class="swiper-brand-car swiper">
      <div class="swiper-wrapper">
        <div
          v-for="tab in CATALOG_SEARCH_TABS"
          :key="tab.title"
          class="swiper-slide"
          @click="sendEventGaTag()"
        >
          <div class="name">
            {{ tab.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- v-if で両要素の存在を保証しているため、props への束縛は非 null アサーションで渡す -->
  <ModalBodyTypeSubModal
    v-if="activeBodyTypeTab && contentsByTabTitle[activeBodyTypeTab.title]"
    :key="activeBodyTypeTab.title"
    :tab-title="activeBodyTypeTab.title"
    :contents="contentsByTabTitle[activeBodyTypeTab.title]!"
  />
  <div class="swiper swiper-contents">
    <div class="swiper-wrapper">
      <!--
        スライドの並びは CATALOG_SEARCH_TABS(= [メーカー, ...BODY_TYPE_TABS]) の構成に対応する。
        ボディタイプ系の子へ 'maker' を渡せないよう、メーカースライドだけ分けて
        以降は BODY_TYPE_TABS を回す
      -->
      <div class="swiper-slide">
        <CardMakerTabContents v-if="showBrandsFlg" />
      </div>
      <div v-for="tab in BODY_TYPE_TABS" :key="tab.title" class="swiper-slide">
        <CardBodyTypeTabContents
          v-if="showBrandsFlg && contentsByTabTitle[tab.title]"
          :tab-title="tab.title"
          :contents="contentsByTabTitle[tab.title]!"
          @change-display-index="changeDisplayIndex"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.swiper-brand-car {
  background-color: #ffffff;
}

.swiper-brand-car .swiper-slide {
  width: auto !important;
  text-align: center !important;
}

.swiper-brand-car .swiper-slide .name {
  font-size: 11px;
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 5px;
  padding: 10px 0px 10px 0px;
}

.swiper-slide-thumb-active::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 0px; /*下線の上下位置調整*/
  display: inline-block;
  width: 52px; /*下線の幅*/
  height: 5px; /*下線の太さ*/
  background-color: #df2020; /*下線の色*/
  transform: translate(-50%); /*位置調整*/
}

.swiper-slide-thumb-active .name {
  color: #df2020 !important;
}

.swiper-box {
  margin-top: 18px;
}

.fw-bold {
  margin-top: 26px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fw-bold img {
  width: 15px;
  margin-left: 3px;
}
</style>
